// src/utils/patientReportPrint.js
//
// The PATIENT's downloadable report (Download PDF on the results page, and the
// chat "download my report" tool). It is deliberately its own document, not a
// printout of the on-screen tabs: a screen is for exploring, a PDF is for
// keeping, sharing with a family doctor, and reading on paper. So this one is
// written for a reader who has never seen the app.
//
// Layout follows the Figma "Health Intelligence Report" export: fixed A4 pages,
// each one a self-contained <section class="pg"> with its own header and footer
// (no running table header), so what you see is what prints:
//
//   01 cover                          (summary + how to read this report)
//   02 Results at a glance            (needs attention, worth watching)
//      Your healthy foundation        (optimal markers; only when switched on)
//   08+ Trajectory                    (only when there are earlier draws)
//      Your Action Items
//      Lifestyle                      (inside the action-plan page)
//      About Previu
//
// Severity words follow the Clinical Operating Architecture v9.8 (Section 1.2,
// canonical threshold registry): a marker is OPTIMAL, WATCHFUL, AMBER, RED or
// CRITICAL; the overall state is GREEN, AMBER, RED or CRITICAL. This file never
// invents its own levels.
//
// The staff report (triageReportPrint.js) keeps its own layout. This builder is
// called from buildPrintHtml() when readOnly is true; everything it needs that
// lives there (marker parsing, trajectory marker choice) is handed in via
// `helpers` so the two files do not import each other.
//
// Fonts: the reference export is set in DejaVu Sans. It is not on every
// machine, so the stack falls back to Verdana (same proportions) and then the
// system sans-serif; nothing is downloaded, so printing works offline.
//
// Colours are literal hex: this window has no app stylesheet. They are declared
// once, as custom properties on :root inside the document, below.

import { isClinicalDetailEnabled } from './reportConfig';
import { systemDisplayName, systemOrganWord } from './systemLabels';
import {
  buildActionPlan,
  buildConvergence,
  buildDiseaseRisks,
  deserializeCheckedState,
  deserializeActionPlan,
  buildMarkerChartData,
  trajectoryCoverage,
  matchesMarkerName,
  isHigherBetter,
  lifestyleReviewers,
  priorDraws,
  MIN_PROJECTION_GAP_MONTHS,
} from './triageUtils';
import { buildOverallTrajectory, overallTrajectoryHtml } from './overallTrajectory';
import { healthScoreCardHtml } from './healthScoreChart';

// ── small helpers ──────────────────────────────────────────────────────────

const esc = (v) => String(v ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—'
    : dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
};
const fmtShort = (d) => {
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? '—'
    : dt.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }).toUpperCase();
};

/** "LDL CHOLESTEROL - DIRECT" -> "LDL Cholesterol - Direct"; keeps short acronyms. */
export const friendlyName = (raw) => {
  const name = String(raw ?? '').trim();
  if (!name) return '—';
  if (name !== name.toUpperCase()) return name; // already mixed case: leave it
  const SPECIAL = { HBA1C: 'HbA1c', 'HS-CRP': 'hs-CRP', APOB: 'ApoB', APOA1: 'ApoA1', 'TG/HDL': 'TG/HDL',
    VLDL: 'VLDL', MCHC: 'MCHC', EGFR: 'eGFR', 'HOMA-IR': 'HOMA-IR', HBSAG: 'HBsAg' };
  const LOWER = new Set(['OF', 'AND', 'TO', 'IN', 'FOR']);
  return name.split(/(\s+)/).map((tok) => {
    if (/^\s+$/.test(tok)) return tok;
    const open = tok.startsWith('(') ? '(' : '';
    const close = tok.endsWith(')') ? ')' : '';
    const core = tok.slice(open.length, tok.length - close.length);
    if (SPECIAL[core]) return open + SPECIAL[core] + close;
    if (/\d/.test(core) || /[/%]/.test(core)) return tok;         // CA 19-9, TG/HDL, %
    if (open) return tok;                                           // (SGPT)
    if (LOWER.has(core)) return core.toLowerCase();
    if (core.length <= 3) return tok;                               // LDL, HDL, TSH, PCV, WBC
    return open + core.charAt(0) + core.slice(1).toLowerCase() + close;
  }).join('');
};

/** Plain-ASCII lab units -> proper typography (x10^3/uL -> ×10³/µL). */
export const friendlyUnit = (raw) => String(raw ?? '')
  .replace(/x\s*10\^3/gi, '×10³').replace(/x\s*10\^6/gi, '×10⁶')
  .replace(/10\^3/g, '10³').replace(/10\^6/g, '10⁶')
  .replace(/\bu(mol|g|L|IU|iu)\b/g, 'µ$1').replace(/\buL\b/g, 'µL').replace(/\bug\b/g, 'µg');

/** Marker level word, per the v9.8 registry. GREEN/NORMAL are shown as OPTIMAL. */
const levelWord = (sev) => {
  const v = String(sev || '').toUpperCase();
  if (['GREEN', 'NORMAL', 'LOW', 'OPTIMAL', 'GOOD'].includes(v)) return 'OPTIMAL';
  if (['CRITICAL', 'CRISIS'].includes(v)) return 'CRITICAL';
  if (['RED', 'POSITIVE'].includes(v)) return 'RED';
  if (['AMBER', 'FLAG'].includes(v)) return 'AMBER';
  if (['WATCHFUL', 'ELEV', 'ELEVATED'].includes(v)) return 'WATCHFUL';
  return v || '—';
};
const cap = (s) => String(s || '').charAt(0).toUpperCase() + String(s || '').slice(1).toLowerCase();

// Per level: pill background / text, and the text colour used for a count or a
// system name. Values are the ones in the reference design.
const LEVEL = {
  OPTIMAL:  { bg: '#e1f0e6', fg: '#347453', num: '#3c825c' },
  WATCHFUL: { bg: '#f3edd8', fg: '#85702e', num: '#9d853d' },
  AMBER:    { bg: '#f6ecd4', fg: '#9d7129', num: '#c17a38' },
  RED:      { bg: '#f7e7e4', fg: '#bd5c56', num: '#bd5c56' },
  CRITICAL: { bg: '#eedfdf', fg: '#923c3c', num: '#923c3c' },
};
const pill = (sev) => {
  const w = levelWord(sev);
  return `<span class="pill ${LEVEL[w] ? 'lv-' + w.toLowerCase() : ''}">${esc(w)}</span>`;
};

// What each severity means, in the reader's words.
const SEVERITY_KEY = [
  ['OPTIMAL',  'In the best range for you. Keep doing what you are doing.'],
  ['WATCHFUL', 'Slightly outside the best range. Good habits and a re-check are usually enough.'],
  ['AMBER',    'Outside the healthy range. Worth acting on and re-testing.'],
  ['RED',      'Clearly out of range. Please review it with your doctor.'],
  ['CRITICAL', 'Needs prompt medical attention.'],
];

// Body systems, as the reader meets them.
// (names live in systemLabels.js, shared with the on-screen Organ Systems panel)
const systemLabel = systemDisplayName;

const joinList = (items) => (items.length <= 1 ? items.join('') : `${items.slice(0, -1).join(', ')}, ${items[items.length - 1]}`);

// ── trajectory chart ───────────────────────────────────────────────────────

/** Smooth monotone curve through points ([x, y] px): no overshoot between draws. */
const smoothPath = (pts) => {
  if (pts.length < 2) return '';
  const n = pts.length;
  const dx = [], m = [];
  for (let i = 0; i < n - 1; i++) { dx.push(pts[i + 1][0] - pts[i][0]); m.push((pts[i + 1][1] - pts[i][1]) / (dx[i] || 1)); }
  const t = [m[0]];
  for (let i = 1; i < n - 1; i++) t.push(m[i - 1] * m[i] <= 0 ? 0 : (m[i - 1] + m[i]) / 2);
  t.push(m[n - 2]);
  let d = `M${pts[0][0].toFixed(1)},${pts[0][1].toFixed(1)}`;
  for (let i = 0; i < n - 1; i++) {
    const h = dx[i] / 3;
    d += ` C${(pts[i][0] + h).toFixed(1)},${(pts[i][1] + t[i] * h).toFixed(1)} ${(pts[i + 1][0] - h).toFixed(1)},${(pts[i + 1][1] - t[i + 1] * h).toFixed(1)} ${pts[i + 1][0].toFixed(1)},${pts[i + 1][1].toFixed(1)}`;
  }
  return d;
};

/** One filled line chart of a marker's real readings, placed by date. */
const areaChart = (cd, tone, { w = 300, h = 100 } = {}) => {
  // Several reports can carry the same draw date; one reading a day, the latest.
  const byDay = new Map();
  cd.drawDates.forEach((d, i) => byDay.set(new Date(d).toDateString(), [new Date(d).getTime(), cd.points[i]]));
  const pairs = [...byDay.values()].sort((a, b) => a[0] - b[0]);
  const vals = pairs.map(p => p[1]);
  const ts = pairs.map(p => p[0]);
  if (vals.length < 2) return '';
  const lo = Math.min(...vals), hi = Math.max(...vals);
  const span = hi - lo || Math.abs(hi) * 0.1 || 1;
  const yMin = lo - span * 0.38, yMax = hi + span * 0.04;
  const t0 = ts[0], t1 = ts[ts.length - 1];
  const px = vals.map((v, i) => [
    ((ts[i] - t0) / ((t1 - t0) || 1)) * w,
    h - ((v - yMin) / (yMax - yMin)) * h,
  ]);
  const line = smoothPath(px);
  const col = tone === 'bad' ? '#bd5c56' : '#438b65';
  const fill = tone === 'bad' ? '#f7e7e4' : '#e0f0e5';
  return `<svg class="achart" viewBox="0 0 ${w} ${h + 2}" preserveAspectRatio="none" width="100%" height="${h * 0.72}pt">
    <path d="${line} L${w},${h} L0,${h} Z" fill="${fill}"/>
    <path d="${line}" fill="none" stroke="${col}" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke"/>
  </svg>`;
};

// ── the document ───────────────────────────────────────────────────────────

const ROWS_PER_OPTIMAL_PAGE = 23;

export const buildPatientPrintHtml = ({
  reportId, patient, triage, markers, missing = [], healthRecord, history: rawHistory = [],
  m3 = { scored: [], unscored: [], systems: {} },
  // Optimal markers (the "healthy foundation") are listed only when asked for.
  showOptimal = false,
  // The patient's health journey: when it has a scored report the Trajectory page draws the Overview tab's Health Score graph.
  journey = null,
  // staff = true builds the clinician's version of the same document: same look and pages, with the clinical summary, every marker level,
  // disease risk, convergence and clinical detail added, and the patient-only pages left out.
  staff = false,
  helpers,
}) => {
  const { parseHistoryMarkers, pickTrajectoryMarkers } = helpers;
  const history = priorDraws(rawHistory, triage, healthRecord?.drawDate || triage?.drawDate || triage?.createdAt);

  const fullName = [patient?.firstName, patient?.lastName].filter(Boolean).join(' ').trim() || 'Patient';
  const year = new Date().getFullYear();

  // Booking id when the report carries one (a lab order), otherwise a short
  // report reference so the cover always has an identifier to quote to support.
  const bookingId = healthRecord?.bookingRef || healthRecord?.bookingId || healthRecord?.partnerOrderId || null;
  const shortRef = 'PRV-' + String(triage?.id || reportId || '').replace(/[^A-Za-z0-9]/g, '').slice(0, 8).toUpperCase();
  const idLabel = bookingId ? 'Booking ID' : 'Report ID';
  const idValue = bookingId || shortRef;

  const drawDate = fmtDate(healthRecord?.drawDate || triage?.drawDate);
  const generated = fmtDate(triage?.createdAt);
  const labName = /thyrocare/i.test(healthRecord?.labName || '') ? 'Thyrocare' : (healthRecord?.labName || '');

  const hasTrajectory = Array.isArray(history) && history.length > 0;
  // Markers the registry classified but policy keeps out of the score (procalcitonin, blood / urine culture) still have a
  // level, and the patient should see them at it.
  const scored = [...(m3.scored || []),
    ...(m3.unscored || []).filter(m => m && m.reason === 'EXCLUDED_FROM_SCORING' && m.severity)];
  const hasScored = scored.length > 0;

  // ── data prep ──────────────────────────────────────────────────────────
  // Marker rows: the Deterministic Model's scored list carries the v9.8 level
  // per marker. If a report has none, fall back to the AI marker statuses.
  const rows = hasScored
    ? scored.map(m => ({ name: m.name || m.code, value: m.value, unit: m.unit, range: m.range, level: levelWord(m.severity), code: m.code }))
    : markers.map(m => ({ name: m.name, value: m.value, unit: m.unit, range: m.range,
        level: m.status === 'bad' ? 'RED' : m.status === 'warn' ? 'AMBER' : 'OPTIMAL' }));
  const inLevels = (levels) => rows.filter(r => levels.includes(r.level));
  const attention = inLevels(['CRITICAL', 'RED']);
  const watching = inLevels(staff ? ['WATCHFUL', 'AMBER'] : ['AMBER']);   // patients: Amber only; staff see Watchful too
  const optimal = showOptimal ? inLevels(['OPTIMAL']) : [];
  const count = (levels) => inLevels(levels).length;

  // Overall state, in the v9.8 words (GREEN / AMBER / RED).
  const lvl = String(triage?.consensusRiskLevel || '').toLowerCase();
  const overall = lvl === 'high' ? 'RED' : lvl === 'moderate' || lvl === 'medium' ? 'AMBER' : lvl === 'low' ? 'GREEN' : null;

  const systems = Object.entries(m3.systems || {});
  const calmSystems = systems.filter(([, sev]) => levelWord(sev) === 'OPTIMAL')
    .map(([s]) => systemOrganWord(s));

  // The ring: a health score out of 100. The triage's consensus risk score runs
  // the other way (higher = riskier), so it is inverted. Without a risk score
  // there is no score and no ring (the hero takes the full width).
  const healthScore = (() => {
    const r = Number(triage?.consensusRiskScore);
    return Number.isFinite(r) && r > 0 && r <= 100 ? Math.round(100 - r) : null;
  })();

  const HERO = {
    GREEN: { pill: 'Most results are healthy',
      head: attention.length + count(['WATCHFUL', 'AMBER']) ? 'You have a strong foundation. A few markers need focused attention.' : 'You have a strong foundation. Keep it going.',
      line: 'Keep up your current habits and re-test as advised.' },
    AMBER: { pill: 'Some results need attention',
      head: 'Most of your picture is steady. A few results are worth acting on.',
      line: 'None of this is an emergency, but it is worth acting on. Start with the steps on the right.' },
    RED:   { pill: 'Some results need prompt attention',
      head: 'Some of your results need prompt attention.',
      line: 'Please review this report with your doctor or Previu care team soon.' },
  };
  const levelWordRisk = String(triage?.consensusRiskLevel || '').toUpperCase();
  const riskNum = Number(triage?.consensusRiskScore);
  const STAFF_PILL = { GREEN: 'Low overall risk', AMBER: 'Moderate overall risk', RED: 'High overall risk' };
  const hero = staff && overall
    ? { pill: STAFF_PILL[overall],
        head: `${attention.length} marker${attention.length === 1 ? '' : 's'} need attention and ${watching.length} ${watching.length === 1 ? 'is' : 'are'} worth watching.`,
        line: `Consensus risk ${Number.isFinite(riskNum) ? `${riskNum}/100 · ` : ''}${levelWordRisk || '—'}. ${rows.length} markers analysed.` }
    : overall ? HERO[overall] : null;

  const flagged = (triage?.highlightedMarkers || []).map(h => {
    const row = rows.find(r => (h.canonicalCode && r.code === h.canonicalCode) || matchesMarkerName(r.name, h.markerName)) || null;
    return { h, row };
  });

  const actions = (() => {
    const base = triage?.actionPlanJson ? deserializeActionPlan(triage.actionPlanJson) : buildActionPlan(triage, missing);
    const list = [...base];
    flagged.forEach(({ h }) => {
      const nm = friendlyName(h.markerName);
      if (list.some(a => String(a.title || '').toLowerCase().includes(nm.toLowerCase()))) return;
      list.push({
        id: `flag-${h.canonicalCode || nm}`, priority: 'P2', category: 'Retest',
        title: `Re-test ${nm}`,
        body: 'Your doctor flagged this marker for follow-up testing. Your Previu care team can help you book it.',
        window: 'As your doctor advises',
      });
    });
    return list;
  })();

  // ── page frame ─────────────────────────────────────────────────────────
  const pages = []; // { cls, label, body, foot? }
  const addPage = (cls, label, body, foot) => pages.push({ cls, label, body, foot });

  const titleBlock = (kicker, title, right = '') => `
<div class="kick">${esc(kicker)}</div>
<div class="title-row"><h2 class="title">${title}</h2>${right}</div>`;
  const chip = (text, tone = 'good', dot = true) => `<span class="chip ${tone}">${dot ? '<i></i>' : ''}${esc(text)}</span>`;

  // ── 01 cover (with the summary and "how to read" on it, as in the design) ──
  // Section numbers mirror what is actually present.
  let n = 0; const num = () => String(++n).padStart(2, '0');
  const sSummary = num(), sMarkers = num();
  const sRisk = staff ? num() : null;
  const sTraj = hasTrajectory ? num() : null;
  const sConv = staff ? num() : null;
  const sActions = num(), sLifestyle = num();
  // Clinical Detail is switched by previu.report.clinical-detail-enabled on the server (off by default); without it the section
  // is left out and takes no number.
  const showClinical = staff && isClinicalDetailEnabled();
  const sClin = showClinical ? num() : null;

  const contents = staff ? [
    [sSummary, 'Clinical Summary', 'Risk score, alerts, findings and the recommendation.'],
    [sMarkers, 'Markers', 'Every marker by level, with what could not be scored.'],
    [sRisk, 'Disease Risk', 'Cardiovascular and metabolic risk, and the intervention window.'],
    ...(hasTrajectory ? [[sTraj, 'Trajectory', 'How key markers have moved across draws.']] : []),
    [sConv, 'Convergence', 'How systems, patterns and root drivers connect.'],
    [sActions, 'Patient Action Items', 'The prioritised steps for the patient.'],
    [sLifestyle, 'Lifestyle', 'The lifestyle plan and who reviewed it.'],
    ...(showClinical ? [[sClin, 'Clinical Detail', 'Navigator actions, safety, monitoring and continuity.']] : []),
  ] : [
    [sSummary, 'Your Summary', 'Your overall result and what to look at first.'],
    [sMarkers, 'Markers', showOptimal ? 'Every marker grouped by how much attention it needs.' : 'The markers that need attention or are worth watching.'],
    ...(hasTrajectory ? [[sTraj, 'Trajectory', 'How key markers have moved and where they are heading.']] : []),
    [sActions, 'Your Action Items', 'The practical next steps, in order.'],
    [sLifestyle, 'Lifestyle', 'Everyday changes, reviewed by your doctor.'],
    ['+', 'About Previu', 'Tests you can order and what makes Previu different.'],
  ];
  const RING_R = 58.35, RING_C = 2 * Math.PI * RING_R;
  const ring = healthScore == null ? '' : `
<svg class="ring" viewBox="0 0 128 128" width="100%" height="100%">
  <circle cx="64" cy="64" r="${RING_R}" fill="none" stroke="rgba(255,255,255,.18)" stroke-width="11.3"/>
  <circle cx="64" cy="64" r="${RING_R}" fill="none" stroke="#92dfae" stroke-width="11.3" stroke-linecap="butt"
    stroke-dasharray="${(RING_C * healthScore / 100).toFixed(1)} ${RING_C.toFixed(1)}" transform="rotate(-90 64 64)"/>
</svg>`;
  const overallTone = overall === 'GREEN' ? 'good' : overall === 'AMBER' ? 'amber' : 'red';
  const cover = `
<section class="pg cover">
  <div class="cover-ring"></div><div class="cover-blob"></div>
  <div class="hdr">
    <div class="brand"><span class="mark light"><svg viewBox="0 0 20 20" width="10.5pt" height="10.5pt"><path d="M10 3.2 11.7 8.3 16.8 10 11.7 11.7 10 16.8 8.3 11.7 3.2 10 8.3 8.3Z" fill="none" stroke="#173f30" stroke-width="1.5" stroke-linejoin="round"/></svg></span><span class="brand-t">previu</span></div>
    <div class="hdr-r">Private &amp; confidential</div>
  </div>
  <div class="cover-kick">${staff ? 'Health Intelligence Report · Clinical view' : 'Health Intelligence Report'}</div>
  ${staff
    ? '<h1 class="cover-title">Clinical report,<br><span>in full.</span></h1><p class="cover-sub">The clinician\'s reading of this blood test: risk, every marker by level, how systems connect, where each marker is heading and the actions to take.</p>'
    : '<h1 class="cover-title">Your health,<br><span>understood.</span></h1><p class="cover-sub">A personalised reading of your blood test, calibrated for Indian bodies. See what your results mean, where each marker is heading, and what to do next.</p>'}

  <div class="cv-sum-k">${esc(sSummary)} / ${staff ? 'Clinical summary' : 'Your summary'}</div>
  ${overall ? `<span class="cv-chip ${overallTone}"><i></i>${staff ? 'Consensus risk' : 'Overall result'}: ${esc(staff ? (levelWordRisk || overall).toLowerCase() : overall.toLowerCase())}</span>` : ''}
  <div class="cv-hero${healthScore == null ? ' wide' : ''}">
    ${hero ? `<span class="cv-pill"><i></i>${esc(hero.pill)}</span>
    <div class="cv-hero-h">${esc(hero.head)}</div>
    <p class="cv-hero-p">${esc(hero.line)}${calmSystems.length ? ` Your ${esc(joinList(calmSystems.slice(0, 4)))} markers are all within normal range.` : ''}</p>` : `<div class="cv-hero-h">Your results at a glance</div>`}
  </div>
  ${healthScore == null ? '' : `<div class="cv-score">
    <div class="cv-ring">${ring}<div class="cv-ring-n">${healthScore}</div><div class="cv-ring-l">Health score</div></div>
    <div class="cv-score-c">${rows.length} markers checked</div>
  </div>`}
  <p class="cv-priv"><b>Private. Secure. Yours for life.</b> Designed to support a more informed conversation with your doctor or Previu care team.</p>

  <div class="cv-how-k">How to read this report</div>
  <div class="cv-toc">${contents.map(([k, t, d]) => `
    <div class="cv-toc-i"><span class="cv-toc-n">${esc(k)}</span><div><div class="cv-toc-t">${esc(t)}</div><div class="cv-toc-d">${esc(d)}</div></div></div>`).join('')}
  </div>

  <div class="cover-card">
    <div class="cc"><div class="cl">Prepared for</div><div class="cname">${esc(fullName)}</div>${labName ? `<div class="clab">Lab · ${esc(labName)}</div>` : ''}</div>
    <div class="cc"><div class="cl">${esc(idLabel)}</div><div class="cv">${esc(idValue)}</div></div>
    <div class="cc"><div class="cl">Sample date</div><div class="cv">${esc(drawDate)}</div></div>
    <div class="cc"><div class="cl">Report date</div><div class="cv">${esc(generated)}</div></div>
  </div>
</section>`;

  // ── 04 results at a glance ─────────────────────────────────────────────
  // Column widths per table, as a share of the page width, taken from the reference.
  const COLS = {
    att:   [20.4, 26.2, 38.4, 15.0],
    watch: [39.4, 18.9, 24.4, 17.3],
    opt1:  [39.2, 19.8, 24.9, 16.1],
    opt2:  [39.6, 21.4, 24.7, 14.3],
  };
  const tbl = (items, { big = false, cols = 'watch' } = {}) => `
<table class="mk${big ? ' big' : ''}">
  <colgroup>${COLS[cols].map(w => `<col style="width:${w}%">`).join('')}</colgroup>
  <thead><tr><th>Marker</th><th>Your result</th><th>Healthy range</th><th>Level</th></tr></thead>
  <tbody>${items.map(r => `
    <tr>
      <td class="nm">${esc(friendlyName(r.name))}</td>
      <td class="val">${esc(r.value ?? '—')}${r.unit ? `${r.unit.trim().startsWith('%') ? '' : ' '}${esc(friendlyUnit(r.unit))}` : ''}</td>
      <td class="rng">${esc(friendlyUnit(r.range || '—'))}</td>
      <td class="lv">${pill(r.level)}</td>
    </tr>`).join('')}
  </tbody>
</table>`;
  const groupHead = (title, k) => `<div class="grp-h"><span>${esc(title)}</span>${k == null ? '' : `<em>${k} MARKER${k === 1 ? '' : 'S'}</em>`}</div>`;
  const needChip = attention.length
    ? chip(`${attention.length} need attention`, 'red') : chip('Nothing needs attention', 'good');
  // The organ-level result, at the start of the results: each body system at its most concerning marker's level.
  const sysCards = systems.length ? `
<p class="lead">Each body system is classified by its most concerning marker.</p>
<div class="sys">${systems.map(([sys, sev]) => `
  <div class="sys-c"><div class="sys-n">${esc(systemLabel(sys))}</div><div class="sys-v" style="color:${(LEVEL[levelWord(sev)] || LEVEL.OPTIMAL).fg}">${esc(cap(levelWord(sev)))}</div></div>`).join('')}
</div>` : '';
  const card = (title, k, body) => `<div class="gcard">${groupHead(title, k)}${body}</div>`;

  // Results pages. Page 2 holds the attention list, then the
  // watching list while there is room; whatever does not fit continues on the
  // pages after it. Heights are the reference's: a row is ~23pt (27pt in the
  // larger "needs attention" table) and each table's heading + header ~58pt.
  const PAGE_ROOM = 640;                                   // pt below the title block
  const ROW_H = 29, CARD_OVER = 96;                        // a row; a card's padding, heading, table header and gap
  const resultPages = [];
  let cur = { att: [], watch: [], attCont: false, watchCont: false };
  const sysCost = systems.length ? 22 + Math.ceil(systems.length / 4) * 52.5 : 0;
  let room = PAGE_ROOM - sysCost;
  const nextPage = () => { resultPages.push(cur); cur = { att: [], watch: [], attCont: false, watchCont: false }; room = PAGE_ROOM; };
  const flow = (rows, key, rowH) => {
    let i = 0;
    while (i < rows.length) {
      const fit = Math.floor((room - CARD_OVER) / rowH);
      if (fit < 1) { nextPage(); continue; }
      const take = rows.slice(i, i + fit);
      cur[key] = take; cur[key + 'Cont'] = i > 0;
      room -= CARD_OVER + take.length * rowH; i += take.length;
      if (i < rows.length) nextPage();
    }
  };
  flow(attention, 'att', ROW_H);
  flow(watching, 'watch', ROW_H);
  // The healthy foundation starts straight after the results, on the same page,
  // when at least three rows fit; the rest continue on its own pages.
  const fitOpt = Math.floor((room - CARD_OVER) / ROW_H);
  const optHere = optimal.length && fitOpt >= 3 ? optimal.slice(0, fitOpt) : [];
  const optRest = optimal.slice(optHere.length);
  cur.opt = optHere;
  resultPages.push(cur);

  // ── staff: clinical summary ────────────────────────────────────────────
  const cut = (t, max) => {
    const x = String(t || '').trim();
    if (x.length <= max) return x;
    const c = x.slice(0, max);
    const end = Math.max(c.lastIndexOf('. '), c.lastIndexOf('.\n'));
    return end > max * 0.5 ? c.slice(0, end + 1) : `${c.trimEnd()}…`;
  };
  if (staff) {
    const det = triage?.deterministicEvaluatedAt && triage?.deterministicRiskScore != null ? triage.deterministicRiskScore : null;
    const modelLine = triage?.activeModels
      ? (triage.activeModels === 'BOTH' ? `Both models · escalates via ${triage.escalationModel === 'DETERMINISTIC' ? 'the Deterministic Model' : 'Claude'}`
        : triage.activeModels === 'DETERMINISTIC' ? 'Deterministic Model only' : 'Claude only') : '';
    const stat = (label, value, sub, color) => `<div class="stat"><div class="stat-l">${esc(label)}</div><div class="stat-v"${color ? ` style="color:${color}"` : ''}>${esc(value)}</div>${sub ? `<div class="stat-s">${esc(sub)}</div>` : ''}</div>`;
    const flaggedRows = flagged.map(({ h, row }) => ({
      name: friendlyName(h.markerName || row?.name || h.canonicalCode || '—'), value: row?.value, unit: row?.unit, range: row?.range, level: row?.level || 'WATCHFUL',
    }));
    addPage('', 'Clinical summary', `
${titleBlock(`${sSummary} / Clinical summary`, 'Where this patient stands', overall ? chip(`Consensus risk: ${(levelWordRisk || overall).toLowerCase()}`, overallTone) : '')}
${triage?.hasAcuteAlert ? `<div class="acute"><b>Acute alert — immediate clinical review required</b>${triage.acuteAlertDetail ? `<p>${esc(triage.acuteAlertDetail)}</p>` : ''}</div>` : ''}
<div class="stats">
  ${stat('Consensus risk', Number.isFinite(riskNum) ? `${riskNum}/100` : '—', levelWordRisk || '')}
  ${stat('Deterministic model', det != null ? String(det) : '—', det != null && triage?.deterministicRiskLevel ? String(triage.deterministicRiskLevel).toUpperCase() : '')}
  ${stat('Markers analysed', String(rows.length), `${attention.length} need attention · ${watching.length} to watch`)}
</div>
${modelLine ? `<p class="lead" style="margin-top:8pt">Model selection: ${esc(modelLine)}</p>` : ''}
${flaggedRows.length ? card('Flagged by the doctor', flaggedRows.length, tbl(flaggedRows, { cols: 'watch' })) : ''}
${triage?.claudeKeyFindings ? `<div class="gcard"><div class="grp-h"><span>Key findings</span></div><p class="body" style="margin-top:7pt">${esc(cut(triage.claudeKeyFindings, 1000))}</p></div>` : ''}
${triage?.claudeRecommendation ? `<div class="gcard"><div class="grp-h"><span>Clinical recommendation</span></div><p class="body" style="margin-top:7pt">${esc(cut(triage.claudeRecommendation, 900))}</p></div>` : ''}
${missing.length ? `<div class="gcard"><div class="grp-h"><span>Missing markers</span><em>NOT IN THE SCORE</em></div><p class="body" style="margin-top:7pt">${esc(cut(missing.join(', '), 500))}</p></div>` : ''}`);
  }

  resultPages.forEach((pg, i) => addPage('', 'Health intelligence report', `
${titleBlock(`${sMarkers} / Markers`, 'Results at a glance', i === 0 ? needChip : chip('Continued', 'good', false))}
${i === 0 ? sysCards : ''}
${pg.att.length ? card(pg.attCont ? 'Needs attention (continued)' : 'Needs attention', pg.attCont ? null : attention.length, tbl(pg.att, { big: true, cols: 'att' })) : ''}
${pg.watch.length ? card(pg.watchCont ? 'Worth watching (continued)' : 'Worth watching', pg.watchCont ? null : watching.length, tbl(pg.watch)) : ''}
${pg.opt && pg.opt.length ? card('Your healthy foundation', optimal.length, tbl(pg.opt)) : ''}`));

  // ── staff: markers shown but not scored ────────────────────────────────
  if (staff) {
    const REASON = {
      UNMAPPED_CODE: 'no canonical code', EXCLUDED_FROM_SCORING: 'excluded by rule',
      SEVERITY_UNRESOLVED: 'could not be classified', NOT_IN_PANEL: 'not in the scoring panel',
    };
    const unscored = (m3.unscored || []).filter(m => m && !(m.reason === 'EXCLUDED_FROM_SCORING' && m.severity));
    const PER = 18;
    for (let i = 0; i < unscored.length; i += PER) {
      addPage('', 'Markers · not scored', `
${titleBlock(`${sMarkers} / Markers`, 'Shown but not scored', i === 0 ? chip(`${unscored.length} not counted`, 'amber', false) : chip('Continued', 'good', false))}
<p class="lead">Listed because "not recognised" and "excluded by rule" are different findings. None of these count towards the score.</p>
<div class="gcard"><table class="mk">
  <colgroup><col style="width:42%"><col style="width:24%"><col style="width:34%"></colgroup>
  <thead><tr><th>Marker</th><th>Your result</th><th>Why not counted</th></tr></thead>
  <tbody>${unscored.slice(i, i + PER).map(m => `
    <tr><td class="nm">${esc(friendlyName(m.name || m.code || '—'))}</td>
    <td class="val">${esc(m.value ?? '—')}${m.unit ? ` ${esc(friendlyUnit(m.unit))}` : ''}</td>
    <td class="rng">${esc(REASON[m.reason] || m.reason || '—')}</td></tr>`).join('')}
  </tbody></table></div>`);
    }
  }

  // ── 05 the healthy foundation ──────────────────────────────────────────
  for (let i = 0; i < optRest.length; i += ROWS_PER_OPTIMAL_PAGE) {
    addPage('', 'Markers · in a good range', `
${titleBlock(`${sMarkers} / Markers`, 'Your healthy foundation', i === 0 && !optHere.length ? chip(`${optimal.length} optimal`, 'good') : chip('Continued', 'good', false))}
<div class="tbl-top">${tbl(optRest.slice(i, i + ROWS_PER_OPTIMAL_PAGE), { cols: i === 0 && !optHere.length ? 'opt1' : 'opt2' })}</div>`);
  }

  // ── staff: disease risk + intervention window ───────────────────────────
  if (staff) {
    // The same estimates the Disease Risk tab shows (triageUtils.buildDiseaseRisks) — this used to be a second copy of the formula
    // that was fed a different marker list and printed 16% where the screen said 80%.
    const TONE = { bad: ['#bd5c56', 'High'], warn: ['#9d7129', 'Elevated'], good: ['#347453', 'Low'] };
    const toneOf = (r) => (r.band === 'High' ? 'bad' : r.band === 'Low' ? 'good' : 'warn');
    const riskCard = (r) => { const t = toneOf(r); return `
<div class="dr"><div class="kick">${esc(r.window)}</div><div class="dr-t">${esc(r.name)}</div>
  <div class="dr-v" style="color:${TONE[t][0]}">${r.pct}%</div><span class="chip ${t === 'bad' ? 'red' : t === 'warn' ? 'amber' : 'good'}">${esc(r.band)}</span>
  <p class="body" style="margin-top:6pt">${esc(cut(r.summary || '', 420))}</p></div>`; };
    const cards = buildDiseaseRisks(triage, markers).risks.map(riskCard);
    const ivDate = triage?.interventionStartDate || patient?.interventionStartDate || null;
    addPage('', 'Disease risk', `
${titleBlock(`${sRisk} / Disease risk`, 'What the markers point to')}
${cards.length ? `<div class="dr-grid">${cards.join('')}</div>` : '<p class="lead">Not enough marker data for a disease risk reading.</p>'}
<div class="gcard"><div class="grp-h"><span>Intervention window</span></div>
  <div class="phases">
    <div><b>Early change</b><span>Subtle shifts begin</span></div>
    <div class="on"><b>Intervention window</b><span>Best time to act</span></div>
    <div><b>Future trajectory</b><span>Two possible paths</span></div>
  </div>
  <p class="body" style="margin-top:8pt">${ivDate
    ? `Intervention starts ${esc(fmtDate(ivDate))}. The 12 months from that date are the window in which a change of direction is still straightforward.`
    : 'No intervention date is set: no report has been drawn since this patient registered, so there is no measured trajectory to anchor a window to. A navigator can set the date by hand.'}</p>
</div>`);
  }

  // ── 03 trajectory ──────────────────────────────────────────────────────
  if (hasTrajectory) {
    const currentDrawDate = healthRecord?.drawDate ? new Date(healthRecord.drawDate) : new Date(triage?.createdAt || Date.now());
    const drawCount = history.length + 1;

    const tableMarkers = pickTrajectoryMarkers(8);
    const priorHdr = [...history].reverse().slice(-8);
    const mover = (m) => {
      const prior = history.map(h => {
        try {
          const pm = parseHistoryMarkers(h).find(x => matchesMarkerName(x.name, m.name));
          return pm ? pm.value : null;
        } catch { return null; }
      });
      const prev = prior.find(v => v != null);
      const curr = parseFloat(m.value), pre = parseFloat(prev);
      const delta = !Number.isNaN(curr) && !Number.isNaN(pre) ? +(curr - pre).toFixed(2) : null;
      // Ratios (TG/HDL) fall when things improve, even though the name contains HDL.
      const higher = /ratio/i.test(m.name) ? false : isHigherBetter(m.name);
      const good = delta === null || delta === 0 ? null : (higher ? delta > 0 : delta < 0);
      return { m, prior, delta, good };
    };
    const movers = tableMarkers.map(mover);
    // The headline numbers favour the markers people act on first (lipids, then
    // uric acid and ApoB), then whatever else moved.
    const FIRST = ['hdl', 'triglycerides', 'uric', 'apo', 'ldl', 'hba1c', 'glucose'];
    const prio = (m) => { const i = FIRST.findIndex(k => String(m.name).toLowerCase().includes(k)); return i < 0 ? FIRST.length : i; };
    const glance = movers.filter(x => x.delta && !/ratio/i.test(x.m.name)).sort((a, b) => prio(a.m) - prio(b.m)).slice(0, 3);
    const statLabel = (m) => {
      const base = friendlyName(m.name).replace(/ — Direct$/i, '').replace(/ \(SGPT\)$/, '');
      const acr = base.match(/^(HDL|LDL|VLDL)\b/i);
      return acr ? `${acr[1]} ${friendlyUnit(m.unit)}`.trim() : base;
    };
    const signed = (d) => `${d > 0 ? '+' : '−'}${Math.abs(d)}`;
    const short = (nm) => friendlyName(nm).replace(/ — Direct$/i, '').replace(/ \(SGPT\)$/, '');
    const uniq = (list) => list.filter((v, i) => list.indexOf(v) === i);
    const byPrio = (list) => [...list].sort((a, b) => prio({ name: a }) - prio({ name: b }));
    const goodNames = byPrio(uniq(movers.filter(x => x.good === true && !/ratio/i.test(x.m.name)).map(x => short(x.m.name))));
    const badNames = byPrio(uniq([
      ...movers.filter(x => x.good === false).map(x => short(x.m.name)),
      ...attention.map(r => short(r.name)),
    ]));
    const glanceLine = [
      goodNames.length ? `Your ${joinList(goodNames.slice(0, 2))} ${goodNames.slice(0, 2).length === 1 ? 'is' : 'are'} moving in a healthy direction.` : '',
      badNames.length ? `${joinList(badNames.slice(0, 2))} ${badNames.slice(0, 2).length === 1 ? 'remains a' : 'remain the'} key signal${badNames.slice(0, 2).length === 1 ? '' : 's'} to review with your physician.` : '',
    ].filter(Boolean).join(' ');

    const overallPal = {
      ink: '#17231d', muted: '#6c7b72', line: '#dce5dc', border: '#dce5dc', dark: '#173f30', good: '#286a49', goodBg: '#e0f0e5',
      bad: '#bd5c56', badBg: '#f7e7e4', amber: '#b8892f', teal: '#0f7468', intervention: '#2563eb', card: '#fffefa', surface: '#fffefa',
      font: "'DejaVu Sans',Verdana,Geneva,sans-serif",
    };
    const scoreGraph = journey ? healthScoreCardHtml(journey, overallPal) : '';
    const overallData = scoreGraph ? null : buildOverallTrajectory(triage, history, currentDrawDate);
    const overallCard = scoreGraph ? `<div class="tover">${scoreGraph}</div>`
      : overallData ? `<div class="tover">${overallTrajectoryHtml(overallData, overallPal)}</div>` : '';
    addPage('traj-intro', 'Trajectory intelligence', `
<div class="tdark">
  <div class="kick light">${esc(sTraj)} / Trajectory</div>
  <div class="tdark-h">A snapshot tells you where you are. A trajectory shows where you're heading.</div>
  <p class="tdark-p">We compared ${drawCount} draws to detect directional change. Your history helps find the window to act while change is still possible.</p>
  <div class="tcaps">
    <div><b>Detect early</b><span>Spot changes before they become serious.</span></div>
    <div><b>See direction</b><span>Understand movement, not just a number.</span></div>
    <div><b>Act in time</b><span>Find the best window for intervention.</span></div>
    <div><b>Track impact</b><span>See whether changes are working.</span></div>
  </div>
</div>
${overallCard}
<div class="tglance">
  <div class="kick">Your trajectory at a glance</div>
  <div class="tstats">
    ${glance.map(x => `<div class="tstat"><div class="tstat-v ${x.good === true ? 'good' : x.good === false ? 'bad' : ''}">${signed(x.delta)}</div><div class="tstat-l">${esc(statLabel(x.m))}</div></div>`).join('')}
    <div class="tstat"><div class="tstat-v">${drawCount}</div><div class="tstat-l">Draws compared</div></div>
  </div>
  ${glanceLine ? `<p class="tglance-p">${esc(glanceLine)}</p>` : ''}
</div>`);

    // chart cards: improving markers on "Then and now", the ones to watch on
    // the next page. Only markers with at least two readings can be drawn.
    const card = (x) => {
      const cd = buildMarkerChartData(x.m.name, markers, history, currentDrawDate, parseHistoryMarkers);
      // A line needs at least three different draw days to say anything.
      if (!cd || new Set(cd.drawDates.map(dt => new Date(dt).toDateString())).size < 3) return '';
      const d = cd.yMax < 20 ? 1 : 0;
      const tone = x.good === false ? 'bad' : 'good';
      const first = cd.drawDates[0], last = cd.drawDates[cd.drawDates.length - 1];
      const trendTxt = Math.abs(cd.slope) < 0.001 ? 'Stable'
        : `Trend ${cd.slope > 0 ? '+' : '−'}${Math.abs(cd.slope * 6).toFixed(2)} ${friendlyUnit(cd.unit)} / 6mo`;
      return `
<div class="cc-card">
  <div class="cc-top"><div class="cc-t">${esc(friendlyName(cd.markerName))}</div>${x.delta ? `<span class="dpill ${tone}">${x.delta > 0 ? '↑' : '↓'} ${Math.abs(x.delta)}</span>` : ''}</div>
  <div class="cc-v">${esc(x.m.value ?? cd.last.toFixed(d))} <small>${esc(friendlyUnit(cd.unit))}</small></div>
  ${areaChart(cd, tone)}
  <div class="cc-f"><span>${esc(fmtDate(first))}</span><b class="${tone}">${esc(trendTxt)}</b><span>${esc(fmtDate(last))}</span></div>
</div>`;
    };
    const improving = movers.filter(x => x.good === true).map(card).filter(Boolean);
    const worsening = movers.filter(x => x.good === false).map(card).filter(Boolean);
    const unreadable = Math.max(0, MIN_PROJECTION_GAP_MONTHS);

    const priorCols = priorHdr;
    addPage('', 'Trajectory intelligence', `
${titleBlock(`${sTraj} / Trajectory`, 'Then and now', chip(`${drawCount} draws compared`, 'good', false))}
<table class="tt">
  <thead><tr><th class="l">Marker</th>${priorCols.map(h => `<th>${esc(fmtShort(h.drawDate || h.createdAt))}</th>`).join('')}<th>Now</th><th>Change</th></tr></thead>
  <tbody>${movers.map(({ m, prior, delta, good }) => {
    const cells = [...prior].reverse().slice(-priorCols.length);
    return `<tr>
      <td class="l nm">${esc(friendlyName(m.name))}</td>
      ${cells.map(v => `<td>${esc(v ?? '—')}</td>`).join('')}
      <td class="now">${esc(m.value ?? '—')}</td>
      <td class="chg ${good === true ? 'good' : good === false ? 'bad' : ''}">${delta === null ? '—' : delta === 0 ? '→ 0' : `${delta > 0 ? '↑' : '↓'} ${Math.abs(delta)}`}</td>
    </tr>`;
  }).join('')}</tbody>
</table>
<p class="note">Change compares your latest result with the one before it. Green means the change is in the healthy direction for that marker; red means the wrong way.</p>
${improving.length ? `<div class="cc-grid">${improving.slice(0, 2).join('')}</div>` : ''}
<div class="avail"><div class="avail-t">Trajectory availability</div><div class="avail-d">Predictions appear once your first and last draws are at least ${unreadable} months apart. The complete trajectory availability notes continue on the next page.</div></div>`);

    const cov = trajectoryCoverage(markers, history, parseHistoryMarkers);
    const nm = (ms) => ms.map(m => friendlyName(typeof m === 'string' ? m : m.name)).join(', ');
    const notes = [
      cov.single.length ? `<p><b>Only one reading so far, so no trajectory yet:</b> ${esc(nm(cov.single))}.</p>` : '',
      cov.earlierOnly.length ? `<p><b>Measured in an earlier draw but not in this one:</b> ${esc(nm(cov.earlierOnly))}.</p>` : '',
    ].join('');
    const insightGood = goodNames.slice(0, 2), insightBad = badNames.slice(0, 2);
    addPage('', 'Trajectory intelligence', `
${titleBlock(`${sTraj} / Trajectory`, 'Signals to keep watching')}
${worsening.length ? `<div class="cc-grid first">${worsening.slice(0, 4).join('')}</div>` : '<p class="lead">No marker is moving the wrong way between your draws.</p>'}
${notes ? `<div class="avail notes"><div class="avail-t">Trajectory availability — complete notes</div>${notes}</div>` : ''}
${insightGood.length || insightBad.length ? `<div class="insight">
  <div class="kick">Previu insight</div>
  <div class="insight-h">${insightBad.length ? 'Your trajectory suggests an early window to act.' : 'Your trajectory is moving the right way.'}</div>
  <p class="insight-p">${[
    insightGood.length ? `${joinList(insightGood)} ${insightGood.length === 1 ? 'has' : 'have'} improved since the previous draw.` : '',
    insightBad.length ? `${joinList(insightBad)} ${insightBad.length === 1 ? 'has' : 'have'} moved the wrong way and ${insightBad.length === 1 ? 'is' : 'are'} worth reviewing with your doctor.` : '',
  ].filter(Boolean).map(esc).join(' ')}</p>
</div>` : ''}`);
  }

  // ── staff: convergence ──────────────────────────────────────────────────
  if (staff) {
    const cv = buildConvergence(markers, buildDiseaseRisks(triage, markers).risks);
    const sevOf = (m) => (m.status === 'bad' ? 'bad' : m.status === 'warn' ? 'warn' : 'good');
    const lvTone = (lvl) => (lvl === 'danger' ? 'bad' : lvl === 'warn' ? 'warn' : 'good');
    const colour = { bad: '#bd5c56', warn: '#9d7129', good: '#347453', off: '#8a958f' };
    const row = (title, detail, t) => `<div class="crow"><b style="color:${colour[t] || colour.good}">${esc(title)}</b><span>${esc(detail || '')}</span></div>`;
    const sysRows = cv.systems.map(sy => {
      const ab = sy.markers.filter(m => m.status !== 'good');
      const t = ab.some(m => m.status === 'bad') ? 'bad' : ab.length ? 'warn' : (sy.markers.length ? 'good' : 'off');
      return row(sy.label, sy.markers.length ? (ab.length ? ab.map(m => `${friendlyName(m.name)} ${m.value ?? ''}`).join(' · ') : `All ${sy.markers.length} optimal`) : 'No lab markers in this panel', t);
    }).join('');
    const patRows = cv.patterns.map(pt => row(pt.label, pt.v, lvTone(pt.level))).join('');
    const drvRows = cv.drivers.map(dr => row(String(dr.label).replace(/\n/g, ' ') + (dr.active ? '' : ' · not indicated'),
      dr.active ? `${dr.v} — feeds ${dr.feeds.join(', ')}` : dr.v, dr.active ? 'bad' : 'off')).join('');
    const outRows = cv.outcomes.map(o => row(o.name, o.why, o.active ? 'bad' : 'off')).join('');
    addPage('', 'Convergence', `
${titleBlock(`${sConv} / Convergence`, "Biomarkers don't move alone", cv.outOfRange.length || cv.borderline.length
  ? chip(`${cv.outOfRange.length} critical · ${cv.borderline.length} borderline`, cv.outOfRange.length ? 'red' : 'amber', false) : chip('All within range', 'good', false))}
<p class="lead">Sleep, inflammation, glucose and cardiovascular risk move together long before symptoms appear. Read left to right: biomarkers, the patterns they form, the root drivers beneath them, and the areas of health they can affect.</p>
<div class="gcard"><div class="grp-h"><span>Biomarker systems</span></div>${sysRows}</div>
<div class="gcard"><div class="grp-h"><span>Patterns</span></div>${patRows}</div>
<div class="gcard"><div class="grp-h"><span>Root drivers</span></div>${drvRows}</div>
<div class="gcard"><div class="grp-h"><span>Areas affected</span></div>${outRows}</div>`);
  }

  // ── 04 action items + 05 lifestyle ─────────────────────────────────────
  const GROUP_LABEL = { P1: 'Do first', P2: 'Next', P3: 'Keep going' };
  const lsItems = Array.isArray(triage?.lifestyleItems) ? triage.lifestyleItems : [];
  const lsReviewers = lifestyleReviewers(lsItems);
  const lsDate = lsReviewers.latest ? fmtDate(lsReviewers.latest) : '';
  const legacy = (triage?.lifestyleRecommendation || '').trim();
  const lifestyleBody = lsItems.length
    ? `<div class="ls-badge">Reviewed and approved by ${esc(lsReviewers.label || 'your doctor')}${lsDate ? ` · ${esc(lsDate)}` : ''}</div>
       ${lsItems.map(it => `<div class="ls-pt"><div class="ls-l">${esc(it.domain || '')}${it.timeWindow ? ` · ${esc(it.timeWindow)}` : ''}</div><div class="ls-m">${esc(it.habitChange)}</div>${it.rationale ? `<div class="ls-w">${esc(it.rationale)}</div>` : ''}</div>`).join('')}
       <p class="ls-note">These changes are a helpful starting point. Always check with your care team before making a major change.${triage?.lifestyleGeneratedAt ? ` Prepared ${esc(fmtDate(triage.lifestyleGeneratedAt))}.` : ''}</p>`
    : legacy
      ? `${legacy.split(/\n\n+/).map(x => x.trim()).filter(Boolean).map(x => `<p class="ls-p">${esc(x)}</p>`).join('')}<p class="ls-note">Check with your care team before making a major change.</p>`
      : `<div class="ls-h">Your personalised plan is being reviewed</div><p class="ls-p">Your personalised lifestyle plan is not ready yet. It is prepared and reviewed by a doctor before it reaches you; download this report again later to include it.</p>`;

  // An action is ~40pt of spacing + its title + one 9.9pt line per ~112 characters.
  const actionH = (x) => 60 + (x.body ? 5 + Math.ceil(String(x.body).length / 112) * 9.9 : 0);
  const lifestyleH = 70 + (lsItems.length
    ? lsItems.reduce((t, it) => t + 22 + Math.ceil(String(it.rationale || '').length / 110) * 8.5, 30)
    : (legacy ? Math.ceil(legacy.length / 110) * 9.9 : 36));
  const actPages = [[]]; let used = 0;
  actions.forEach((x) => {
    if (used + actionH(x) > PAGE_ROOM && actPages[actPages.length - 1].length) { actPages.push([]); used = 0; }
    actPages[actPages.length - 1].push(x); used += actionH(x);
  });
  const lsBlock = `
<div class="ls">
  <div class="kick">${esc(sLifestyle)} / Lifestyle</div>
  ${lifestyleBody}
</div>`;
  const lsFits = used + lifestyleH + 19 <= PAGE_ROOM;
  let actNo = 0;
  actPages.forEach((list, pi) => addPage('', 'Personal action plan', `
${titleBlock(`${sActions} / ${staff ? 'Patient action items' : 'Your action items'}`, 'A simple plan, in the right order', pi === 0 ? '' : chip('Continued', 'good', false))}
<div class="acts">${list.length ? list.map((a) => `
  <div class="act">
    <span class="act-n">${++actNo}</span>
    <div><div class="act-t">${esc(a.title || '')}</div>${a.body ? `<div class="act-d">${esc(a.body)}</div>` : ''}</div>
    ${a.window ? `<div class="act-w"><span>${esc(GROUP_LABEL[a.priority] || 'Next')}</span>${esc(a.window)}</div>` : '<div></div>'}
  </div>`).join('') : '<p class="lead">No action items on this report.</p>'}</div>
${pi === actPages.length - 1 && lsFits ? lsBlock : ''}`));
  if (!lsFits) {
    addPage('', 'Personal action plan', `
${titleBlock(`${sLifestyle} / Lifestyle`, 'Your lifestyle plan')}
<div class="ls first">${lsBlock.replace(/^\s*<div class="ls">|<\/div>\s*$/g, '')}</div>`);
  }

  // ── staff: clinical detail (layers 11-16) ───────────────────────────────
  if (showClinical) {
    let l11 = null;
    try { l11 = JSON.parse(String(triage?.claudeRawResponse || '{}').replace(/```json|```/g, '').trim())?.layers_11_18 || null; } catch { l11 = null; }
    const has = l11 && (l11.intervention_plan || l11.clinical_briefing || l11.safety_flags || l11.monitoring_protocol || l11.memory_integration || l11.communication_protocol);
    if (!has) {
      addPage('', 'Clinical detail', `
${titleBlock(`${sClin} / Clinical detail`, 'Navigator and safety notes')}
<div class="gcard"><p class="body">No clinical detail was generated for this report. It predates the Layer 11-18 enrichment call, or that call failed.</p></div>`);
    } else {
      const arr = (v) => (Array.isArray(v) ? v : []);
      const iv = l11.intervention_plan || {}, br = l11.clinical_briefing || {}, sf = l11.safety_flags || {};
      const mon = l11.monitoring_protocol || {}, mem = l11.memory_integration || {}, comm = l11.communication_protocol || {};
      const navActions = arr(iv.navigator_actions);
      const flags = arr(sf.red_flag_details), contra = arr(sf.contraindications_noted);
      const repeat = arr(mon.repeat_biomarkers), wear = arr(mon.wearable_parameters), trig = arr(mon.symptom_log_triggers);
      const channel = { NAVIGATOR_WHATSAPP: 'Navigator WhatsApp', IN_APP: 'In-app message', NONE: 'None' }[comm.channel] || comm.channel;
      const para = (label, text) => (text ? `<p class="body" style="margin-top:5pt"><b>${esc(label)}</b> ${esc(cut(text, 600))}</p>` : '');
      addPage('', 'Clinical detail', `
${titleBlock(`${sClin} / Clinical detail`, 'Navigator and safety notes', iv.follow_up_recommended_days != null ? chip(`Follow up in ${iv.follow_up_recommended_days} day(s)`, 'good', false) : '')}
<div class="gcard"><div class="grp-h"><span>Navigator actions</span><em>LAYER 11</em></div>
${navActions.length ? navActions.slice(0, 6).map(a => `<div class="crow"><b>${esc(a.priority || '—')} · ${esc(cut(a.action || '', 140))}</b><span>${esc(a.timeframe || '—')}</span></div>
  ${a.rationale ? `<p class="body" style="padding:2pt 0 4pt">${esc(cut(a.rationale, 260))}</p>` : ''}`).join('') : '<p class="body" style="margin-top:6pt">No navigator actions recommended.</p>'}</div>
${(br.headline || br.key_concern) ? `<div class="gcard"><div class="grp-h"><span>Clinical briefing</span><em>LAYER 12${br.doctor_urgency ? ` · ${esc(String(br.doctor_urgency).replace('_', ' '))}` : ''}</em></div>
  ${para('', br.headline)}${para('', br.key_concern)}${para('', br.south_asian_risk_context)}</div>` : ''}
<div class="gcard"><div class="grp-h"><span>Safety screening</span><em>LAYER 13</em></div>
  ${sf.red_flags_present && flags.length ? `<p class="body" style="margin-top:5pt;color:var(--bad)"><b>Red flags:</b> ${esc(cut(flags.join('; '), 400))}</p>` : ''}
  <p class="body" style="margin-top:5pt">${contra.length ? esc(contra.join(' · ')) : 'No contraindications noted.'}</p></div>
${mem.continuity_note ? `<div class="gcard"><div class="grp-h"><span>Continuity</span><em>LAYER 14</em></div>${para('', mem.continuity_note)}</div>` : ''}
${comm.channel ? `<div class="gcard"><div class="grp-h"><span>Communication protocol</span><em>LAYER 15</em></div>
  <p class="body" style="margin-top:5pt">Channel: ${esc(channel)} · Timing: ${comm.send_immediately ? 'Immediate' : 'Queued'}${comm.physician_notify ? ' · Physician notification recommended' : ''}. Routing decision only; not wired to an automatic send.</p></div>` : ''}
<div class="gcard"><div class="grp-h"><span>Monitoring protocol</span><em>LAYER 16${mon.repeat_frequency_days != null ? ` · REPEAT IN ${esc(mon.repeat_frequency_days)} DAY(S)` : ''}</em></div>
  ${repeat.length || wear.length || trig.length
    ? `${repeat.length ? `<p class="body" style="margin-top:5pt"><b>Repeat biomarkers:</b> ${esc(repeat.join(', '))}</p>` : ''}${wear.length ? `<p class="body"><b>Wearable parameters:</b> ${esc(wear.join(', '))}</p>` : ''}${trig.length ? `<p class="body"><b>Watch for:</b> ${esc(trig.join(', '))}</p>` : ''}`
    : '<p class="body" style="margin-top:5pt">No monitoring protocol specified.</p>'}</div>
${iv.patient_message_summary ? `<div class="gcard"><div class="grp-h"><span>Patient message</span></div>${para('', iv.patient_message_summary)}</div>` : ''}`);
    }
  }

  // ── about Previu (marketing) ───────────────────────────────────────────
  const PANELS = [
    ['T1 · Essential', 'Your metabolic and heart baseline',
     'Fasting glucose, HbA1c, fasting insulin, HOMA-IR, full lipid profile, hs-CRP, CBC, uric acid, and blood pressure.'],
    ['T2 · Standard', 'Doctor-grade preventive check',
     'Everything in T1, plus ApoB, homocysteine, liver enzymes, kidney function, thyroid, Vitamin D, B12, ferritin, and hepatitis B and C screens.'],
    ['T3 · Precision', 'The deepest look',
     'Everything in T2, plus thyroid hormones, sex and stress hormones, Lp(a), ApoA1, and IGF-1.'],
    ['Tumour marker panels', 'Gender-specific screens',
     'Women: AFP, Beta-hCG, CA 125, CA 15-3, CA 19-9, CEA. Men: total and free PSA, AFP, Beta-hCG, CA 19-9, CEA.'],
  ];
  const DIFFS = [
    ['Calibrated for Indian bodies', 'India-specific thresholds account for South Asian risk.'],
    ['Trajectory, not snapshots', 'Follow every marker across draws and see where it is heading.'],
    ['The whole picture', 'Understand how organ systems connect and find the root driver.'],
    ['Confounds checked first', 'Fasting, medicines, and recent exercise are considered.'],
    ['Doctor-reviewed AI', 'Lifestyle guidance is prepared by AI and reviewed by a doctor.'],
    ['A care team that follows up', 'Named navigators and doctors act on your results.'],
  ];
  if (!staff) {
  addPage('about', 'About Previu', `
<div class="kick">About Previu</div>
<h2 class="title">Know your health.<br>Before it needs knowing.</h2>
<p class="lead about-lead">Tests you can order through your Previu portal.</p>
<div class="panels">${PANELS.map(([t, s, d]) => `
  <div class="panel"><div class="panel-k">${esc(t)}</div><div class="panel-t">${esc(s)}</div><div class="panel-d">${esc(d)}</div></div>`).join('')}
</div>
<div class="diffs">${DIFFS.map(([t, d], i) => `
  <div class="diff"><b>${i + 1} · ${esc(t)}</b><span>${esc(d)}</span></div>`).join('')}
</div>
<div class="kick lvl-kick">What the levels mean</div>
<div class="lvl-row">${SEVERITY_KEY.map(([w, d]) => `
  <div class="lvl lv-${w.toLowerCase()}"><div class="lvl-t">${w}</div><div class="lvl-d">${esc(d)}</div></div>`).join('')}
</div>
<div class="cta"><div class="cta-t">Ready for your next check?</div><div class="cta-d">Open the Previu portal or app to track your health, or message your care team · previu.health</div></div>`,
  `For information only; not a diagnosis. Please discuss your results with your doctor or Previu care team. © ${year} Previu Health.`);
  }

  // ── assemble ───────────────────────────────────────────────────────────
  const shell = (p, i) => {
    const no = String(i + 2).padStart(2, '0');
    return `
<section class="pg ${p.cls}">
  <div class="hdr">
    <div class="brand"><span class="mark">P</span><span class="brand-t">previu</span></div>
    <div class="hdr-r">${esc(p.label)}</div>
  </div>
  <div class="pg-body">${p.body}</div>
  <div class="ftr"><span>${p.foot ? esc(p.foot) : 'Private &amp; confidential · previu.health'}</span><span>${no}</span></div>
</section>`;
  };

  return `<!DOCTYPE html>
<html lang="en"><head><meta charset="utf-8"/>
<title>Previu Health Intelligence Report — ${esc(fullName)}</title>
<style>
:root{
  --bg:#f4f7f2;--card:#fffefa;--line:#dce5dc;--dark:#173f30;--ink:#17231d;--muted:#6c7b72;--body:#596961;--label:#75837b;
  --mint:#92dfae;--ring:#59ac7c;--soft:#e8eee8;--tint:#e3ede3;--good:#347453;--bad:#bd5c56;--good-bg:#e1f0e6;--bad-bg:#f7e7e4;
  --dk2:#294e40;
}
*{box-sizing:border-box;margin:0;padding:0}
@page{size:210mm 297mm;margin:0}
html,body{background:var(--bg)}
body{font-family:"DejaVu Sans",Verdana,Geneva,sans-serif;color:var(--ink);font-size:6pt;line-height:1.4;
  -webkit-print-color-adjust:exact;print-color-adjust:exact}
.pg{position:relative;width:210mm;height:297mm;overflow:hidden;background:var(--bg);break-after:page;page-break-after:always}
.pg:last-child{break-after:auto;page-break-after:auto}
.pg-body{position:absolute;left:45.35pt;right:45.35pt;top:99pt}

/* header + footer */
.hdr{position:absolute;left:45.35pt;right:45.35pt;top:47.5pt;height:19.5pt;display:flex;align-items:center;justify-content:space-between}
.brand{display:flex;align-items:center;gap:5.5pt}
.mark{width:19.5pt;height:19.5pt;border-radius:5.5pt;background:var(--dark);color:#fff;font-weight:700;font-size:9.5pt;display:flex;align-items:center;justify-content:center}
.mark.light{background:#fff}
.brand-t{font-weight:700;font-size:10.9pt;color:var(--ink);position:relative;top:1pt}
.hdr-r{font-weight:700;font-size:5.6pt;letter-spacing:.186em;color:var(--muted);text-transform:uppercase}
.ftr{position:absolute;left:45.35pt;right:45.35pt;top:809.7pt;display:flex;justify-content:space-between;font-size:5.6pt;color:var(--label);text-transform:none}
.ftr span:first-child{text-transform:uppercase}
.about .ftr span:first-child{text-transform:none}

/* type */
.kick{font-weight:700;font-size:6pt;letter-spacing:.175em;text-transform:uppercase;color:var(--label)}
.kick.light{color:#9dbbac}
.title-row{display:flex;justify-content:space-between;align-items:flex-end;margin-top:6.6pt}
.title{font-weight:700;font-size:18pt;line-height:20.4pt;color:var(--ink);margin-top:6.6pt}
.title-row .title{margin-top:0}
.lead{font-size:6.4pt;color:var(--muted);margin:14.5pt 0 0}
.h3{font-weight:700;font-size:9.5pt;color:var(--ink);margin-bottom:9pt}
.body{font-size:6.75pt;line-height:10.75pt;color:var(--body)}
.chip{display:inline-flex;align-items:center;gap:3.5pt;font-weight:700;font-size:6pt;padding:3.3pt 6.8pt;margin-bottom:-.2pt;border-radius:99pt;white-space:nowrap}
.chip i{width:3pt;height:3pt;border-radius:50%;background:currentColor;display:inline-block}
.chip.good{background:#e0f0e5;color:#286a49}
.chip.red{background:var(--bad-bg);color:var(--bad)}
.chip.amber{background:#f6ecd4;color:#9d7129}

/* pills */
.pill{display:inline-block;font-weight:700;font-size:5pt;padding:1.1pt 4.5pt;line-height:1.16;border-radius:99pt;white-space:nowrap;vertical-align:middle}
.pill.lv-optimal{background:#e1f0e6;color:#347453}
.pill.lv-watchful{background:#f3edd8;color:#85702e}
.pill.lv-amber{background:#f6ecd4;color:#9d7129}
.pill.lv-red{background:#f7e7e4;color:#bd5c56}
.pill.lv-critical{background:#eedfdf;color:#923c3c}

/* cover */
.pg.cover{background:var(--dark);color:#fff}
.cover .brand-t{color:#fff}
.cover .hdr-r{color:#9db9aa}
.cover-ring{position:absolute;left:301pt;top:-126pt;width:439pt;height:439pt;border-radius:50%;border:.75pt solid rgba(255,255,255,.2)}
.cover-blob{position:absolute;left:326pt;top:635pt;width:322pt;height:207pt;background:#234f3d;border-radius:161pt 161pt 0 0/100pt 100pt 0 0}
.cover-kick{position:absolute;left:45.35pt;top:117pt;font-weight:700;font-size:6pt;letter-spacing:.25em;text-transform:uppercase;color:#a9c9b9}
.cover-title{position:absolute;left:45.35pt;top:132pt;font-weight:400;font-size:33pt;line-height:35pt;letter-spacing:-.03em;color:#fff}
.cover-title span{color:var(--mint)}
.cover-sub{position:absolute;left:45.35pt;top:210pt;width:404pt;font-size:8.2pt;line-height:12.6pt;color:#c4d7cd}
.cover-card{position:absolute;left:45.35pt;right:45.35pt;top:725pt;height:65pt;border-radius:19pt;background:rgba(255,255,255,.07);
  border:.75pt solid rgba(255,255,255,.2);display:grid;grid-template-columns:146.5pt 110.3pt 110.3pt 1fr;padding:17pt 22.7pt 0}
.cl{font-weight:700;font-size:4.8pt;letter-spacing:.2em;text-transform:uppercase;color:#98b6a7}
.cv{font-weight:700;font-size:6.75pt;color:#fff;margin-top:2.6pt}
.cname{font-weight:700;font-size:12pt;color:#fff;margin-top:1.3pt}
.clab{font-size:5.25pt;color:#a5bcb0;margin-top:3.5pt}

/* cover: summary + how to read */
.cv-sum-k{position:absolute;left:45.35pt;top:253pt;font-weight:700;font-size:5.6pt;letter-spacing:.2em;text-transform:uppercase;color:#a9c9b9}
.cv-chip{position:absolute;right:45.35pt;top:246pt;display:inline-flex;align-items:center;gap:3.5pt;font-weight:700;font-size:5.6pt;padding:4pt 8pt;border-radius:99pt;background:rgba(255,255,255,.1);color:#d6e8de}
.cv-chip i{width:3pt;height:3pt;border-radius:50%;background:var(--mint)}
.cv-chip.amber i{background:#e0b25a}.cv-chip.red i{background:#e08a84}
.cv-hero{position:absolute;left:45.35pt;top:273pt;width:385pt;height:128pt;border-radius:20pt;background:rgba(255,255,255,.1);border:.75pt solid rgba(255,255,255,.12);padding:16pt 16pt 0}
.cv-hero.wide{width:504.9pt}
.cv-pill{display:inline-flex;align-items:center;gap:4pt;background:rgba(255,255,255,.1);color:#cce4d7;font-weight:700;font-size:5.4pt;padding:3.4pt 7.5pt;border-radius:99pt}
.cv-pill i{width:3pt;height:3pt;border-radius:50%;background:var(--mint)}
.cv-hero-h{font-size:14.5pt;line-height:16.8pt;margin-top:13pt;color:#fff;width:320pt}
.cv-hero-p{font-size:5.6pt;line-height:8.6pt;color:#bfd2c8;margin-top:10pt;width:340pt}
.cv-score{position:absolute;left:438pt;top:273pt;width:112.2pt;height:128pt;border-radius:20pt;background:rgba(255,255,255,.1);border:.75pt solid rgba(255,255,255,.12)}
.cv-ring{position:absolute;left:16pt;top:18pt;width:80pt;height:80pt}
.cv-ring .ring{position:absolute;inset:0}
.cv-ring-n{position:absolute;left:0;right:0;top:20pt;text-align:center;font-weight:700;font-size:17pt;line-height:22pt;color:#fff}
.cv-ring-l{position:absolute;left:0;right:0;top:46pt;text-align:center;font-weight:700;font-size:3.6pt;letter-spacing:.2em;text-transform:uppercase;color:#a9c9b9}
.cv-score-c{position:absolute;left:0;right:0;top:103pt;text-align:center;font-size:4.6pt;color:#a9c9b9}
.cv-priv{position:absolute;left:45.35pt;top:406pt;font-size:4.7pt;color:#9db9aa}
.cv-priv b{color:#d6e8de}
.cv-how-k{position:absolute;left:45.35pt;top:429pt;font-weight:700;font-size:5.2pt;letter-spacing:.2em;text-transform:uppercase;color:#a9c9b9}
.cv-toc{position:absolute;left:45.35pt;right:45.35pt;top:442pt;display:grid;grid-template-columns:repeat(3,1fr);gap:6.5pt 5.8pt}
.cv-toc-i{display:flex;gap:9pt;align-items:center;height:39pt;border-radius:10pt;background:rgba(255,255,255,.08);border:.75pt solid rgba(255,255,255,.12);padding:0 10pt 0 10pt}
.cv-toc-n{flex:none;width:20pt;height:20pt;border-radius:50%;background:var(--mint);color:var(--dark);font-weight:700;font-size:4.8pt;display:flex;align-items:center;justify-content:center}
.cv-toc-t{font-weight:700;font-size:5.8pt;color:#fff}
.cv-toc-d{font-size:4.5pt;line-height:6.4pt;color:#b3cdbf;margin-top:2pt}
.lvl-row{display:grid;grid-template-columns:repeat(5,1fr);gap:6pt;margin-top:8pt}
.lvl{border-radius:12pt;padding:8pt 8pt 0;height:46pt}
.lvl-t{font-weight:700;font-size:6pt}
.lvl-d{font-size:4.4pt;line-height:6pt;color:var(--muted);margin-top:3pt}
.lvl.lv-optimal{background:#e1f0e6}.lvl.lv-optimal .lvl-t{color:#347453}
.lvl.lv-watchful{background:#f3edd8}.lvl.lv-watchful .lvl-t{color:#85702e}
.lvl.lv-amber{background:#f6ecd4}.lvl.lv-amber .lvl-t{color:#9d7129}
.lvl.lv-red{background:#f7e7e4}.lvl.lv-red .lvl-t{color:#bd5c56}
.lvl.lv-critical{background:#eedfdf}.lvl.lv-critical .lvl-t{color:#923c3c}
/* 04+ markers */
.sys{display:grid;grid-template-columns:repeat(4,1fr);gap:8.9pt;margin-top:13.6pt}
.sys-c{height:43.5pt;background:var(--card);border:.75pt solid var(--line);border-radius:12pt;padding:11pt 12pt 0}
.sys-n{font-size:5.4pt;color:var(--muted)}
.sys-v{font-weight:700;font-size:6.4pt;margin-top:5.5pt}
.stats{display:grid;grid-template-columns:repeat(3,1fr);gap:8.9pt;margin-top:16pt}
.stat{background:var(--card);border:.75pt solid var(--line);border-radius:14pt;padding:11pt 13pt;height:54pt}
.stat-l{font-weight:700;font-size:4.9pt;letter-spacing:.15em;text-transform:uppercase;color:var(--muted)}
.stat-v{font-weight:700;font-size:15pt;margin-top:4pt}
.stat-s{font-size:5pt;color:var(--muted);margin-top:2pt}
.acute{background:#8c2a2f;color:#fff;border-radius:14pt;padding:12pt 16pt;margin-top:14pt}
.acute b{font-size:7.4pt}.acute p{font-size:6pt;margin-top:3pt;color:#f3d9d6}
.crow{display:flex;justify-content:space-between;gap:12pt;padding:4.6pt 0;border-bottom:.75pt solid var(--line);font-size:5.8pt}
.crow b{font-size:6.2pt;flex:none;max-width:55%}.crow span{color:var(--muted);text-align:right}
.dr-grid{display:grid;grid-template-columns:1fr 1fr;gap:10pt;margin-top:16pt}
.dr{background:var(--card);border:.75pt solid var(--line);border-radius:17pt;padding:14pt 15pt}
.dr-t{font-weight:700;font-size:9pt;margin-top:3pt}
.dr-v{font-weight:700;font-size:24pt;line-height:28pt;margin-top:4pt}
.phases{display:grid;grid-template-columns:repeat(3,1fr);gap:7pt;margin-top:9pt}
.phases div{border:.75pt solid var(--line);border-radius:11pt;padding:8pt 10pt}
.phases div.on{background:#f6ecd4;border-color:#e2c98f}
.phases b{display:block;font-size:6.2pt}.phases span{display:block;font-size:5.2pt;color:var(--muted);margin-top:2pt}
.gcard{background:var(--card);border:.75pt solid var(--line);border-radius:17pt;padding:14pt 14pt 6pt;margin-top:15pt}
.gcard:first-of-type{margin-top:19pt}
.grp-h{display:flex;justify-content:space-between;align-items:baseline}
.grp-h span{font-weight:700;font-size:7.2pt}
.grp-h em{font-style:normal;font-weight:700;font-size:5.6pt;letter-spacing:.2em;color:var(--muted)}
.mk{width:100%;table-layout:fixed;border-collapse:collapse;margin-top:11pt}
.mk.big{margin-top:11pt}
.mk th{text-align:left;font-weight:700;font-size:4.9pt;letter-spacing:.13em;text-transform:uppercase;color:var(--label);padding:0 6pt 6.9pt 6pt;border-bottom:.75pt solid var(--line)}
.mk td{padding:1.4pt 6pt 0;height:29pt;border-bottom:.75pt solid var(--line);font-size:5.5pt;color:var(--ink);vertical-align:middle;overflow-wrap:anywhere}
.mk td.nm{font-weight:700;font-size:5.85pt}
.mk td.rng{color:var(--ink)}
.mk td.val,.mk td.rng{padding-top:0}
.mk td.lv{padding-top:1.8pt;white-space:nowrap}
.mk td.nm{line-height:1.25}
.mk.big td.nm,.mk.big td.lv{padding-top:3pt}
.mk.big td.val,.mk.big td.rng{padding-top:1.4pt}
.mk.big td{height:29pt;font-size:6pt}
.mk.big td.val{font-weight:700}
.mk.big td.nm{font-size:6.4pt}
.mk tr:last-child td{border-bottom:none}
.tbl-top .mk{margin-top:31.1pt}


/* 08 trajectory */
.pg.traj-intro .pg-body{top:98pt}
.tdark{background:var(--dark);border-radius:28pt;padding:23.5pt 22.7pt 0;height:265.6pt;color:#fff}
.tdark-h{font-weight:700;font-size:21.7pt;line-height:24.5pt;width:300pt;margin-top:12.7pt}
.tdark-p{font-size:6.6pt;line-height:11pt;color:#bed3c8;width:350pt;margin-top:10.9pt}
.tcaps{display:grid;grid-template-columns:repeat(4,1fr);gap:9pt;margin-top:13.2pt}
.tcaps div{background:var(--dk2);border-radius:14pt;height:53.5pt;padding:11pt 11pt 0}
.tcaps b{display:block;font-size:6pt}
.tcaps span{display:block;font-size:5.3pt;line-height:9pt;color:#a9c2b6;margin-top:4.4pt}
.tover{margin-top:16pt;font-size:6.8pt}
.tglance{background:var(--card);border:.75pt solid var(--line);border-radius:28pt;margin-top:16pt;padding:20pt 23pt 0;height:140pt}
.tglance .kick{letter-spacing:.115em}
.tstats{display:grid;grid-template-columns:repeat(4,1fr);gap:9pt;margin-top:13pt}
.tstat{height:57.6pt;border:.75pt solid var(--line);border-radius:17pt;padding:11.5pt 14pt 0;background:var(--card)}
.tstat-v{font-weight:700;font-size:15.7pt;line-height:18pt}
.tstat-v.good{color:#347453}.tstat-v.bad{color:#bd5c56}
.tstat-l{font-weight:700;font-size:4.9pt;letter-spacing:.15em;text-transform:uppercase;color:var(--muted);margin-top:4pt;white-space:nowrap}
.tglance-p{font-size:6.4pt;line-height:9.5pt;color:var(--muted);margin-top:16pt}

.tt{width:100%;border-collapse:collapse;margin-top:31.5pt}
.tt th{font-weight:700;font-size:4.75pt;letter-spacing:.12em;text-transform:uppercase;color:var(--label);padding:0 9.68pt 7pt;text-align:center;border-bottom:.75pt solid var(--line);white-space:nowrap;width:1%}
.tt th.l,.tt td.l{text-align:left;padding:0 0 0 3pt;width:auto}
.tt th.l{padding-bottom:7pt}
.tt td.l{padding-top:8.35pt;padding-bottom:8.35pt}
.tt td{height:25.5pt;text-align:center;font-size:4.8pt;color:var(--ink);border-bottom:.75pt solid var(--line);padding:8.35pt 9.68pt;white-space:nowrap}
.tt td.nm{font-weight:700;font-size:6.4pt;white-space:normal;line-height:8.4pt}
.tt td.now{font-weight:400}
.tt td.chg{font-size:4.8pt}
.tt td.good{color:#438b65}.tt td.bad{color:#c16964}
.tt tr:last-child td{border-bottom:none}
.note{font-size:5.26pt;color:var(--muted);margin-top:11pt}
.cc-grid{display:grid;grid-template-columns:1fr 1fr;gap:14.4pt;margin-top:15.3pt}
.cc-card{height:159.7pt;background:var(--card);border:.75pt solid var(--line);border-radius:22pt;padding:11pt 14.1pt 0;position:relative}
.cc-top{display:flex;justify-content:space-between;align-items:flex-start}
.cc-t{font-weight:700;font-size:9.45pt;line-height:12pt}
.dpill{font-weight:700;font-size:5.6pt;padding:3.6pt 8.5pt;border-radius:99pt}
.dpill.good{background:#e2f1e7;color:#438b65}.dpill.bad{background:#f7e7e4;color:#bd5c56}
.cc-v{font-weight:700;font-size:13.5pt;line-height:17pt;margin-top:2.3pt}
.cc-grid.first{margin-top:23.4pt}
.cc-v small{font-weight:400;font-size:5.25pt;color:var(--muted);margin-left:-1.4pt}
.achart{display:block;margin-top:12.5pt}
.cc-f{position:absolute;left:14.1pt;right:14.1pt;top:134.8pt;display:flex;justify-content:space-between;font-size:4.9pt;color:var(--muted)}
.cc-f b{font-weight:700}.cc-f b.good{color:#347453}.cc-f b.bad{color:#bd5c56}
.avail{background:var(--soft);border-radius:19pt;padding:14pt 14.2pt 15pt;margin-top:17.1pt}
.avail-t{font-weight:700;font-size:6.4pt}
.avail-d{font-size:5.4pt;color:var(--muted);margin-top:4.1pt}
.avail.notes{padding:16.3pt 17.4pt 15pt;margin-top:20.1pt;min-height:104.5pt}
.avail.notes .avail-t{font-size:6.8pt}
.avail.notes p{font-size:5pt;line-height:7.2pt;color:var(--muted);margin-top:4.5pt}
.avail.notes p b{font-weight:700}
.insight{background:#e0ebe1;border-radius:19pt;padding:17.5pt 17.4pt 0;margin-top:14.8pt;height:85pt}
.insight .kick{letter-spacing:.13em}
.insight-h{font-weight:700;font-size:11.7pt;line-height:14pt;margin-top:3.1pt}
.insight-p{font-size:6.4pt;line-height:9pt;color:var(--muted);margin-top:6pt;width:360pt}

/* action items */
.acts{margin-top:20pt}
.act{display:grid;grid-template-columns:26pt 1fr auto;column-gap:16pt;padding:16pt 18pt 16pt 16pt;margin-bottom:9pt;background:var(--card);border:.75pt solid var(--line);border-radius:20pt;align-items:start}
.act-n{width:26pt;height:26pt;border-radius:50%;background:var(--dark);color:#fff;font-weight:700;font-size:6.8pt;display:flex;align-items:center;justify-content:center;margin-top:0}
.act-t{font-weight:700;font-size:9.45pt;line-height:12pt}
.act-d{font-size:6.4pt;line-height:9.9pt;color:var(--muted);margin-top:5pt;width:360pt}
.act-w{text-align:right;font-weight:700;font-size:6.6pt;text-transform:uppercase;color:var(--ink);padding-top:0}
.act-w span{display:block;font-size:5pt;letter-spacing:.15em;color:var(--muted);margin-bottom:4pt}
.ls{background:var(--tint);border-radius:22pt;padding:20pt 19.65pt 20pt;margin-top:19.2pt}
.ls-h{font-weight:700;font-size:9.45pt;margin-top:4.4pt}
.ls-p{font-size:6.4pt;line-height:9.9pt;color:var(--muted);margin-top:3.8pt}
.ls-badge{display:inline-block;font-weight:700;font-size:5pt;letter-spacing:.1em;text-transform:uppercase;background:#e1f0e6;color:#347453;border-radius:99pt;padding:3pt 8pt;margin-top:7pt}
.ls-pt{margin-top:8pt;break-inside:avoid}
.ls-l{font-weight:700;font-size:5pt;letter-spacing:.1em;text-transform:uppercase;color:var(--label)}
.ls-m{font-weight:700;font-size:6.6pt;margin-top:1.5pt}
.ls-w{font-size:6pt;color:var(--muted);margin-top:1.5pt}
.ls.first{margin-top:25pt}
.ls-note{font-size:5.6pt;color:var(--muted);margin-top:9pt}

/* about */
.about-lead{margin-top:23.5pt}
.panels{display:grid;grid-template-columns:1fr 1fr;gap:15pt 14.4pt;margin-top:16pt}
.panel{height:112pt;background:var(--card);border:.75pt solid var(--line);border-radius:24pt;padding:19pt 17pt 0}
.panel-k{font-weight:700;font-size:5.25pt;letter-spacing:.15em;text-transform:uppercase;color:#69927a}
.panel-t{font-weight:700;font-size:9.45pt;margin-top:6.7pt}
.panel-d{font-size:6pt;line-height:9pt;color:var(--muted);margin-top:6.8pt}
.diffs{display:grid;grid-template-columns:repeat(3,1fr);gap:8pt 8pt;margin-top:14pt}
.diff{background:var(--tint);border-radius:14pt;height:44pt;padding:11pt 12pt 0}
.diff b{display:block;font-size:5.8pt}
.diff span{display:block;font-size:4.8pt;line-height:6.6pt;color:var(--muted);margin-top:3pt}
.cta{margin-top:18pt;background:var(--dark);border-radius:19pt;height:63pt;padding:16pt 18pt 0;color:#fff}
.cta-t{font-weight:700;font-size:14.5pt;line-height:19pt}
.cta-d{font-size:5.6pt;color:#bcd1c6;margin-top:5pt}
.lvl-kick{margin-top:16pt}
</style></head>
<body>
${cover}
${pages.map(shell).join('')}
</body></html>`;
};
